import React, { useEffect, useState } from "react";

import { Trans, useTranslation } from "react-i18next";
import { Tag, X } from "lucide-react";

import moment from "@/modules/moment.module";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { useCouponCode } from "@/hooks/subscriptions/useCouponCode";
import { notify } from "@/components/framework/notify";
import { useSubscription } from "@/hooks/subscriptions/useSubscription";
import { useUpdateStripeCustomer } from "@/hooks/subscriptions/useUpdateStripeCustomer";
import { useCompanyIdFromUrl } from "@/hooks/companies/useCompanyIdFromUrl";
import { useDialogModal } from "@/hooks/useDialogModal";
import { bool } from "prop-types";

const CouponField = ({ disabled }) => {
  const { t } = useTranslation();
  const { confirm, dialogElement } = useDialogModal();
  const [couponCode, setCouponCode] = useState();

  const companyId = useCompanyIdFromUrl();

  const { data: subscriptionRequest, isLoading: isLoadingSubscription } =
    useSubscription();
  const subscription = subscriptionRequest?.data;
  const existingDiscount = subscription?.stripeData?.discount;
  const existingCouponCode = existingDiscount?.coupon?.id;
  const couponName = existingDiscount?.coupon?.name;

  const {
    data: verifiedCouponCodeRequest,
    isLoading: isVerifyingCouponCode,
    error: couponCodeError,
  } = useCouponCode(couponCode);

  const verifiedCouponCode = verifiedCouponCodeRequest?.data;

  const { mutate: updateStripeCustomer, isPending: isUpdatingStripeCustomer } =
    useUpdateStripeCustomer(companyId);

  const onCouponError = () => {
    setCouponCode("");
    notify("error", t("subscriptions.error.failed_to_verify_coupon"));
  };

  useEffect(() => {
    if (!couponCodeError) return;
    onCouponError();
  }, [couponCodeError]);

  useEffect(() => {
    if (!verifiedCouponCode?.id) return;

    updateStripeCustomer(
      { coupon: verifiedCouponCode.id },
      { onError: onCouponError },
    );
  }, [verifiedCouponCode?.id]);

  const onClickRemoveCouponCode = async () => {
    const isRemovalConfirmed = await confirm(
      t("subscriptions.coupon.confirm_removal.title"),
      <Trans i18nKey="subscriptions.coupon.confirm_removal.body" />,
      {
        variant: "destructive",
        submitText: t("subscriptions.coupon.confirm_removal.title"),
      },
    );

    if (!isRemovalConfirmed) return;

    setCouponCode("");
    updateStripeCustomer({ coupon: null });
  };

  const isLoading =
    isVerifyingCouponCode || isLoadingSubscription || isUpdatingStripeCustomer;

  const discountEndTime =
    existingDiscount?.end && moment.unix(existingDiscount.end).format("LL");

  return (
    <>
      <div>
        {existingCouponCode ? (
          <div>
            <label>{t("subscriptions.discount_code")}</label>
            <div className="border-success flex h-[52px] justify-between rounded-lg border">
              <div className="grid w-full grid-cols-[20px_1fr_30px] items-center gap-2 px-2">
                <Tag size={20} className="text-success" />
                <div className="flex flex-col items-start">
                  <span className="text-success truncate font-bold">
                    {couponName}
                  </span>
                  <span className="text-foreground truncate">
                    {discountEndTime
                      ? `${t("generic.expires")} ${discountEndTime}`
                      : t("generic.never_expires")}
                  </span>
                </div>
                <Button
                  disabled={disabled}
                  variant="ghost"
                  onClick={onClickRemoveCouponCode}
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Input
            label={t("subscriptions.discount_code")}
            className="h-[52px]"
            disabled={disabled || isLoading}
            value={couponCode}
            onChange={(evt) => setCouponCode(evt.target.value)}
            placeholder={t("subscriptions.account.placeholder.coupon")}
            isLoading={isLoading}
          />
        )}
      </div>
      {dialogElement}
    </>
  );
};

CouponField.propTypes = {
  disabled: bool,
};

CouponField.defaultProps = {
  disabled: false,
};

export default CouponField;
